import React from 'react';

import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AvatarComponent from 'react-avatar';

import { baseColors, baseVariants } from './Avatar.collections';

import type { AvatarProperties } from './Avatar.types';

const colors = baseColors.flatMap(color =>
  baseVariants.map(variant => color[variant]),
);

export const Avatar: React.FC<AvatarProperties> = ({
  dataTest = 'avatar',
  fullName,
  email,
  size,
  round,
  children,
  style,
  textSizeRatio,
  textMarginRatio,
  onClick,
  sx,
}) => {
  const theme = useTheme();

  const { bgColor, fgColor } = React.useMemo(() => {
    const identification = fullName || email;
    // `!` вместо `?? 0` (AF-351): спред строки отдаёт непустые кодовые точки,
    // `codePointAt(0)` на них всегда число. Ветка `?? 0` была недостижима.
    const stringHashedAsNumber = [...(identification || 'NoName')].reduce(
      (accum, value) => (value.codePointAt(0) as number) + accum,
      identification?.length || 0,
    );

    const backgroundColor = colors[stringHashedAsNumber % colors.length];
          /*
       * Цвет текста считает тема, а не своя функция (AF-44).
       *
       * Раньше здесь был `getContrastYiq` — выбор чёрного или белого по
       * яркости YIQ. YIQ и WCAG расходятся: на списке из 156 цветов аватара
       * (13 палитр MUI × 12 оттенков) они дают разный ответ в 23 случаях, и
       * во всех двадцати трёх YIQ выбирает белый с контрастом ниже 4.5 —
       * от 2.24:1 до 4.56:1, тогда как тёмный дал бы 4.6–8.1. Худший случай
       * `#00c853`: белый 2.24:1 практически нечитаем.
       *
       * `getContrastText` берёт порог из темы (`contrastThreshold: 4.5`),
       * то есть согласован с остальной палитрой и переживёт смену цветов.
       * Список 23 цветов и метод замера — docs/architecture/design-system.md.
       */
      const foregroundColor = theme.palette.getContrastText(backgroundColor);

    return { bgColor: backgroundColor, fgColor: foregroundColor };
  }, [email, fullName, theme]);

  return (
    <Box
      data-testid={dataTest}
      onClick={onClick}
      sx={[{
        display: 'flex'
      }, ...(Array.isArray(sx) ? sx : [sx])]}>
      <AvatarComponent
        size={size}
        round={round}
        name={fullName}
        email={email}
        style={style}
        textSizeRatio={textSizeRatio}
        textMarginRatio={textMarginRatio}
        color={bgColor}
        fgColor={fgColor}
      />
      {children}
    </Box>
  );
};
